import { nanoid } from '@reduxjs/toolkit';
import React from 'react';
import { ArrowBackIos, ArrowForwardIos, ArrowLeft, ArrowRight } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { EAllowedDateDirection } from '../types';

const today = new Date();
today.setHours(0, 0, 0, 0);

interface CalendarProps {
	displayDate: Date;
	startDate: Date | null;
	endDate: Date | null;
	onDateSelect: (date: Date) => void;
	onMonthChange: (newDate: Date) => void;
	allowedDateDirection?: EAllowedDateDirection;
	side?: 'left' | 'right' | 'both';
}

export const Calendar: React.FC<CalendarProps> = ({
	displayDate,
	startDate,
	endDate,
	onDateSelect,
	onMonthChange,
	allowedDateDirection = EAllowedDateDirection.ALL,
	side = 'both',
}) => {
	const year = displayDate.getFullYear();
	const month = displayDate.getMonth();

	const daysInMonth = new Date(year, month + 1, 0).getDate();
	const firstDayOfMonth = new Date(year, month, 1).getDay();
	const adjustedFirstDay = (firstDayOfMonth === 0 ? 7 : firstDayOfMonth) - 1;

	const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
	const blanks = Array(adjustedFirstDay).fill(null);

	const handlePrevMonth = () => {
		onMonthChange(new Date(year, month - 1, 1));
	};

	const handleNextMonth = () => {
		onMonthChange(new Date(year, month + 1, 1));
	};

	const isStart = (day: number) => {
		if (!startDate) return false;
		const currentDate = new Date(year, month, day);
		currentDate.setHours(0, 0, 0, 0);
		const startDateTime = new Date(startDate).setHours(0, 0, 0, 0);
		return currentDate.getTime() === startDateTime;
	};

	const isEnd = (day: number) => {
		if (!endDate) return false;
		const currentDate = new Date(year, month, day);
		currentDate.setHours(0, 0, 0, 0);
		const endDateTime = new Date(endDate).setHours(0, 0, 0, 0);
		return currentDate.getTime() === endDateTime;
	};

	const isToday = (day: number) => {
		const currentDate = new Date(year, month, day);
		currentDate.setHours(0, 0, 0, 0);
		return currentDate.getTime() === today.getTime();
	};

	const isSelected = (day: number) => {
		const currentDate = new Date(year, month, day);
		currentDate.setHours(0, 0, 0, 0);
		const startDateTime = startDate ? new Date(startDate).setHours(0, 0, 0, 0) : null;
		const endDateTime = endDate ? new Date(endDate).setHours(0, 0, 0, 0) : null;

		return (
			(startDateTime && currentDate.getTime() === startDateTime) ||
			(endDateTime && currentDate.getTime() === endDateTime)
		);
	};

	const isInRange = (day: number) => {
		const currentDate = new Date(year, month, day);
		currentDate.setHours(0, 0, 0, 0);
		const startDateTime = startDate ? new Date(startDate).setHours(0, 0, 0, 0) : null;
		const endDateTime = endDate ? new Date(endDate).setHours(0, 0, 0, 0) : null;
		return (
			startDateTime &&
			endDateTime &&
			currentDate.getTime() > startDateTime &&
			currentDate.getTime() < endDateTime
		);
	};

	const isDisabled = (day: number): boolean => {
		const currentDate = new Date(year, month, day);
		currentDate.setHours(0, 0, 0, 0);

		if (allowedDateDirection === EAllowedDateDirection.PAST) {
			return currentDate.getTime() >= today.getTime();
		}
		if (allowedDateDirection === EAllowedDateDirection.FUTURE) {
			return currentDate.getTime() <= today.getTime();
		}
		return false;
	};

	// TODO: Add styles for the calendar
	const cellStyle = (day: number): React.CSSProperties => {
		const baseStyle: React.CSSProperties = {
			padding: '10px',
			textAlign: 'center',
			cursor: 'pointer',
			border: '1px solid #eee',
			color: '#006293',
		};
		if (isSelected(day)) {
			return {
				...baseStyle,
				backgroundColor: '#006293',
				color: 'white',
				borderRadius: isStart(day) ? '12px 0 0 12px' : '0 12px 12px 0',
			};
		}

		if (isToday(day)) {
			return {
				...baseStyle,
				// backgroundColor: '#00629370',
				fontWeight: '900',
			};
		}

		if (isDisabled(day)) {
			return {
				...baseStyle,
				cursor: 'not-allowed',
				backgroundColor: '#00629310',
				color: '#00629340',
			};
		}
		if (isInRange(day)) {
			return { ...baseStyle, backgroundColor: '#00629340' };
		}

		return baseStyle;
	};

	const weekdays = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

	return (
		<div
			style={{
				width: '300px',
				// border: '1px solid #ccc',
				padding: '10px',
				borderRadius: '12px',
				backgroundColor: '#fff',
			}}
		>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginBottom: '10px',
				}}
			>
				{side === 'left' || side === 'both' ? (
					<IconButton onClick={handlePrevMonth}>
						<ArrowBackIos color="primary" />
					</IconButton>
				) : (
					<div style={{ width: '24px' }} />
				)}
				<span style={{ fontSize: '18px', fontWeight: 'bold', color: '#006293' }}>
					{displayDate.toLocaleString('en-US', { month: 'long', year: 'numeric' })}
				</span>
				{side === 'right' || side === 'both' ? (
					<IconButton onClick={handleNextMonth}>
						<ArrowForwardIos color="primary" />
					</IconButton>
				) : (
					<div style={{ width: '24px' }} />
				)}
			</div>
			<div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
				{weekdays.map((day: string) => (
					<div key={nanoid()} style={{ fontWeight: 'bold', textAlign: 'center', color: '#006293' }}>
						{day}
					</div>
				))}
				{blanks.map((_, index: number) => (
					<div key={nanoid()} />
				))}
				{days.map((day: number) => (
					<div
						key={day}
						style={cellStyle(day)}
						onClick={() => {
							const selectedDate = new Date(year, month, day);
							selectedDate.setHours(0, 0, 0, 0);
							if (!isDisabled(day)) {
								onDateSelect(selectedDate);
							}
						}}
					>
						{day}
					</div>
				))}
			</div>
		</div>
	);
};
